export type HrCapability = { number: string; name: string; line: string };

export const hrCapabilities: HrCapability[] = [
  { number: "01", name: "Implementations", line: "New HRIS, payroll, time, and benefits platforms — scoped, configured, tested, and launched correctly." },
  { number: "02", name: "Implementation Rescue", line: "We take over implementations that are late, over budget, or heading to go-live wrong — and get them back on track." },
  { number: "03", name: "Configuration", line: "We review how your system is set up, find what’s wrong, and correct it — earnings, deductions, accruals, rules, and security." },
  { number: "04", name: "System Optimization", line: "Remove workarounds, clean up data, and make the system do what you bought it to do." },
  { number: "05", name: "Integrations", line: "Connect HRIS, payroll, time, benefits, recruiting, and finance so data moves correctly without re-keying." },
  { number: "06", name: "Vendor Conversions", line: "Move platforms with clean data, mapped history, and no missed paychecks." },
  { number: "07", name: "Testing & Go-Live", line: "Test plans, parallel payroll, cutover, and hands-on support through the first live cycles." },
  { number: "08", name: "Ongoing System Support", line: "Administration, changes, upgrades, and fixes — handled by people who already know your setup." },
];

export type SystemType = { name: string; line: string };

export const systemTypes: SystemType[] = [
  { name: "HRIS", line: "Core employee records, org structure, and self-service" },
  { name: "Payroll platforms", line: "Earnings, deductions, taxes, and pay rules" },
  { name: "Time & attendance", line: "Schedules, punches, and overtime rules" },
  { name: "Benefits integrations", line: "Enrollment feeds and deductions" },
  { name: "ATS / recruiting", line: "Hire-to-onboard data handoff" },
  { name: "Finance integrations", line: "GL mapping and journal entries" },
  { name: "Workforce management", line: "Labor planning and scheduling" },
];

export const systemSide: string[] = [
  "Configuration and pay rules",
  "Integrations and file feeds",
  "Security and roles",
  "Reporting and data",
];

export const operationSide: string[] = [
  "Payroll cycles and approvals",
  "Benefits and deductions",
  "Time, overtime, and pay policy",
  "Year-end and tax",
];

export type EcosystemNode = { id: string; name: string; detail: string; note?: string };

export const ecosystemNodes: EcosystemNode[] = [
  { id: "hris", name: "HRIS", detail: "Employee record, job, org, and pay rate", note: "Source of truth" },
  { id: "time", name: "Timekeeping", detail: "Schedules, punches, and pay rules" },
  { id: "payroll", name: "Payroll", detail: "Gross-to-net, tax, and payment" },
  { id: "benefits", name: "Benefits", detail: "Enrollment, eligibility, and carriers" },
  { id: "finance", name: "Finance", detail: "General ledger and cost allocation" },
];

export type EcosystemHandoff = {
  from: string;
  to: string;
  label: string;
  moves: string;
  breaks: string;
  check: string;
};

export const ecosystemHandoffs: EcosystemHandoff[] = [
  {
    from: "hris",
    to: "time",
    label: "Employee & job data",
    moves: "New hires, job changes, schedules, and pay rates — each with an effective date.",
    breaks: "A job or rate change lands after the time period closes, so hours are paid under the old rules.",
    check: "Effective-dated changes reviewed against every pay-period cutoff.",
  },
  {
    from: "time",
    to: "payroll",
    label: "Approved hours",
    moves: "Approved hours, overtime, premiums, and leave taken.",
    breaks: "Overtime and differentials are calculated one way in time and another in payroll. Late approvals slip through.",
    check: "Hours tied to the payroll register, every cycle, before release.",
  },
  {
    from: "payroll",
    to: "benefits",
    label: "Deductions",
    moves: "Benefit deductions, eligibility, and coverage changes.",
    breaks: "Enrollment and payroll drift apart after open enrollment — arrears, refunds, and carrier invoices that don’t match.",
    check: "Deductions reconciled to enrollment and carrier bills each month.",
  },
  {
    from: "benefits",
    to: "finance",
    label: "Costs & GL",
    moves: "Employer costs, accruals, and the payroll journal.",
    breaks: "GL mapping falls behind org changes, so journals stop tying to the register.",
    check: "Register-to-GL tie-out before every close.",
  },
];

export type ImplementationStep = { number: string; name: string; line: string; start: number; end: number };

export const implementationWeeks = 24;

export const implementationSteps: ImplementationStep[] = [
  { number: "01", name: "Requirements", line: "How you actually pay people, written down before anything is configured.", start: 0, end: 4 },
  { number: "02", name: "Configuration", line: "Built to your pay rules, policies, and approvals — not the vendor’s defaults.", start: 3, end: 11 },
  { number: "03", name: "Integrations", line: "Every feed mapped, tested, and owned.", start: 6, end: 14 },
  { number: "04", name: "Testing & parallel", line: "Real payrolls run side by side until the numbers tie out.", start: 11, end: 19 },
  { number: "05", name: "Go-live", line: "Cutover planned by the day, with us in the room.", start: 19, end: 20 },
  { number: "06", name: "Stabilization", line: "We stay through the first live cycles and fix what surfaces.", start: 20, end: 24 },
];

export const parallelRuns: { week: number; label: string }[] = [
  { week: 15, label: "P1" },
  { week: 17.5, label: "P2" },
];

export type MigrationRow = { handoff: string; before: string; after: string };

export const migrationRows: MigrationRow[] = [
  { handoff: "Recruiting → HRIS", before: "New hires re-keyed by HR", after: "Integrated, validated at hire" },
  { handoff: "HRIS → Timekeeping", before: "Weekly spreadsheet, uploaded by hand", after: "Integrated, effective-dated" },
  { handoff: "Timekeeping → Payroll", before: "Exported, then adjusted in a spreadsheet", after: "Integrated, tied out each cycle" },
  { handoff: "Payroll ↔ Benefits", before: "Deductions keyed manually", after: "Scheduled feed, reconciled monthly" },
  { handoff: "Payroll → Finance", before: "Journal rebuilt in a spreadsheet", after: "Mapped GL file, tied out before close" },
];

export type MigrationMetric = { label: string; before: string; after: string };

export const migrationMetrics: MigrationMetric[] = [
  { label: "Manual touchpoints per cycle", before: "14", after: "3" },
  { label: "Spreadsheets in the process", before: "9", after: "1" },
  { label: "Days from close to pay", before: "4", after: "2" },
];
